import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMe } from "@/auth/useMe";
import { Dialog } from "@/components/Dialog";
import { Button, FormField, Input, Seg, Select } from "@/components/ui";
import type {
  ManagedTemplate,
  Slot,
  TemplateCategory,
  TemplateInput,
  TemplateLanguage,
  VariableSource,
} from "./model";
import {
  LANGUAGES,
  MAX_BUTTONS,
  SOURCES,
  blankNumbers,
  insertBlank,
  previewParts,
  problemsIn,
  removeBlank,
  slugify,
} from "./model";
import { useSaveTemplate } from "./queries";

/**
 * *Nueva plantilla*, and editing one — the template manager's form.
 *
 * Written for someone who has never heard of `{{1}}`: a blank is inserted by
 * what it holds ("Nombre del cliente"), the form numbers it, and the preview
 * beside the text shows it filled with an example. What Meta's review would
 * refuse is said before the template leaves, and the server says it again.
 */

type ButtonRow = { key: number; text: string };

type Draft = {
  name: string;
  category: TemplateCategory;
  language: TemplateLanguage;
  header: string;
  body: string;
  footer: string;
  buttons: ButtonRow[];
  variables: Slot[];
};

let lastKey = 0;
/** A key that survives removing the row above it, which an index does not. */
function nextKey(): number {
  lastKey += 1;
  return lastKey;
}

function isLanguage(value: string): value is TemplateLanguage {
  return (LANGUAGES as readonly string[]).includes(value);
}

function draftFrom(template?: ManagedTemplate): Draft {
  return {
    name: template?.name ?? "",
    category: template?.category === "MARKETING" ? "MARKETING" : "UTILITY",
    language: template && isLanguage(template.language) ? template.language : "es_MX",
    header: template?.header ?? "",
    body: template?.body ?? "",
    footer: template?.footer ?? "",
    buttons: (template?.buttons ?? []).map((text) => ({ key: nextKey(), text })),
    variables: [...(template?.variables ?? [])],
  };
}

/** One slot per blank the body holds, whatever the mapping held before. */
function slotsFor(body: string, variables: readonly Slot[]): Slot[] {
  const numbers = blankNumbers(body);
  const count = numbers.length > 0 ? Math.max(...numbers) : 0;
  return Array.from({ length: count }, (_, index) => variables[index] ?? "");
}

export function PlantillaForm({
  template,
  onClose,
}: {
  /** Pass to edit rather than create. */
  template?: ManagedTemplate;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const save = useSaveTemplate();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(template));
  const [showProblems, setShowProblems] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // A template made in WhatsApp Manager with an image or a link button: the
  // form would drop what it cannot show, so only its blanks are mapped here.
  const wordsLocked = template !== undefined && !template.words_editable;
  const slots = slotsFor(draft.body, draft.variables);
  const buttons = draft.buttons.map((row) => row.text.trim()).filter(Boolean);
  const problems = problemsIn(
    {
      name: draft.name,
      header: draft.header,
      body: draft.body,
      footer: draft.footer,
      buttons,
      variables: slots,
    },
    template === undefined,
  );

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  function addBlank(source: VariableSource) {
    const at = bodyRef.current?.selectionStart ?? draft.body.length;
    const next = insertBlank(draft.body, at, slots, source);
    setDraft((previous) => ({
      ...previous,
      body: next.body,
      variables: next.variables,
    }));
    // Back to the text, just after the blank, so the sentence carries on.
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(next.cursor, next.cursor);
    });
  }

  function dropBlank(n: number) {
    const next = removeBlank(draft.body, slots, n);
    setDraft((previous) => ({
      ...previous,
      body: next.body,
      variables: next.variables,
    }));
  }

  function example(source: VariableSource): string {
    return source === "agent_name" && me?.name
      ? me.name
      : t(`templates.sourceExamples.${source}`);
  }

  function submit() {
    if (problems.length > 0) {
      setShowProblems(true);
      return;
    }
    const body: TemplateInput = {
      ...(template === undefined
        ? { name: slugify(draft.name), category: draft.category }
        : {}),
      // Always sent: the schema requires it. Meta never changes a template's
      // language, so on an edit the server ignores it.
      language: draft.language,
      header: draft.header.trim(),
      body: draft.body.trim(),
      footer: draft.footer.trim(),
      buttons,
      variables: slots.filter((slot): slot is VariableSource => slot !== ""),
    };
    save.mutate(template === undefined ? { body } : { id: template.id, body }, {
      onSuccess: onClose,
    });
  }

  const parts = previewParts(draft.body, slots, example);

  return (
    <Dialog
      open
      onClose={onClose}
      kicker={t("templates.kicker")}
      title={template ? template.name : t("templates.newTitle")}
      footer={
        <>
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {save.isPending
              ? t("templates.sending")
              : template
                ? t("templates.saveChanges")
                : t("templates.submit")}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {save.isError ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {save.error.message}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="flex min-w-0 flex-col gap-5">
          {template === undefined ? (
            <>
              <FormField
                label={t("templates.nameLabel")}
                hint={
                  draft.name.trim()
                    ? t("templates.nameSavedAs", { name: slugify(draft.name) || "—" })
                    : t("templates.nameHint")
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    data-autofocus=""
                    maxLength={120}
                    value={draft.name}
                    placeholder={t("templates.namePlaceholder")}
                    onChange={(event) => set("name", event.target.value)}
                  />
                )}
              </FormField>
              <div>
                <p className="kicker mb-2 text-mute-600">{t("templates.category")}</p>
                <Seg<TemplateCategory>
                  name="template-category"
                  value={draft.category}
                  onChange={(value) => set("category", value)}
                  options={[
                    { value: "UTILITY", label: t("templates.categories.UTILITY") },
                    { value: "MARKETING", label: t("templates.categories.MARKETING") },
                  ]}
                />
                <p className="mt-1.5 text-[11px] text-mute-600">
                  {t(`templates.categoryHints.${draft.category}`)}
                </p>
              </div>
              <FormField label={t("templates.language")}>
                {(id) => (
                  <Select
                    id={id}
                    value={draft.language}
                    onChange={(event) => {
                      if (isLanguage(event.target.value)) {
                        set("language", event.target.value);
                      }
                    }}
                  >
                    {LANGUAGES.map((language) => (
                      <option key={language} value={language}>
                        {t(`templates.languages.${language}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </>
          ) : wordsLocked ? (
            <p className="border-2 border-dashed border-mute-400 bg-panel px-4 py-3 text-[13px] text-mute-600">
              {t("templates.wordsLocked")}
            </p>
          ) : (
            <p className="text-[13px] text-mute-600">{t("templates.reviewAgain")}</p>
          )}

          <FormField label={t("templates.header")} hint={t("templates.optional")}>
            {(id) => (
              <Input
                id={id}
                maxLength={60}
                disabled={wordsLocked}
                value={draft.header}
                onChange={(event) => set("header", event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("templates.body")} hint={t("templates.bodyHint")}>
            {(id) => (
              <textarea
                id={id}
                ref={bodyRef}
                rows={5}
                maxLength={1024}
                className="input resize-y"
                disabled={wordsLocked}
                value={draft.body}
                placeholder={t("templates.bodyPlaceholder")}
                onChange={(event) => set("body", event.target.value)}
              />
            )}
          </FormField>

          {wordsLocked ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-mute-600">{t("templates.insert")}</span>
              {SOURCES.map((source) => (
                <Button
                  key={source}
                  className="px-2 py-1 text-xs"
                  onClick={() => addBlank(source)}
                >
                  {t(`templates.sourceLabels.${source}`)}
                </Button>
              ))}
            </div>
          )}

          {slots.length > 0 ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="kicker mb-1 text-mute-600">
                {t("templates.blanks")}
              </legend>
              {slots.map((slot, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <code className="w-12 text-sm">{`{{${index + 1}}}`}</code>
                  <Select
                    className="w-auto"
                    aria-label={t("templates.blankN", { n: index + 1 })}
                    value={slot}
                    onChange={(event) =>
                      set(
                        "variables",
                        slots.map((other, position) =>
                          position === index ? (event.target.value as Slot) : other,
                        ),
                      )
                    }
                  >
                    <option value="" disabled>
                      {t("templates.pickSource")}
                    </option>
                    {SOURCES.map((source) => (
                      <option key={source} value={source}>
                        {t(`templates.sourceLabels.${source}`)}
                      </option>
                    ))}
                  </Select>
                  {wordsLocked ? null : (
                    <Button
                      variant="ghost"
                      aria-label={t("templates.removeBlankN", { n: index + 1 })}
                      onClick={() => dropBlank(index + 1)}
                    >
                      {t("templates.remove")}
                    </Button>
                  )}
                </div>
              ))}
            </fieldset>
          ) : null}

          <FormField label={t("templates.footer")} hint={t("templates.optional")}>
            {(id) => (
              <Input
                id={id}
                maxLength={60}
                disabled={wordsLocked}
                value={draft.footer}
                onChange={(event) => set("footer", event.target.value)}
              />
            )}
          </FormField>

          <QuickReplies
            rows={draft.buttons}
            disabled={wordsLocked}
            onChange={(rows) => set("buttons", rows)}
          />

          {showProblems && problems.length > 0 ? (
            <ul
              role="alert"
              className="flex flex-col gap-1 border-2 border-brand px-3 py-2 text-sm"
            >
              {problems.map((problem) => (
                <li key={problem}>{t(`templates.problems.${problem}`)}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <aside
          aria-label={t("templates.preview")}
          className="self-start lg:sticky lg:top-0"
        >
          <p className="kicker mb-2 text-mute-600">{t("templates.preview")}</p>
          <div className="border-2 border-rule bg-panel p-3 text-sm">
            {draft.header.trim() ? (
              <p className="mb-1 font-semibold">{draft.header}</p>
            ) : null}
            <p className="whitespace-pre-wrap">
              {parts.length > 0 ? (
                parts.map((part, index) =>
                  part.blank ? (
                    <mark key={index} className="bg-brand-100 px-0.5 text-ink">
                      {part.text}
                    </mark>
                  ) : (
                    <span key={index}>{part.text}</span>
                  ),
                )
              ) : (
                <span className="text-mute-500">{t("templates.previewEmpty")}</span>
              )}
            </p>
            {draft.footer.trim() ? (
              <p className="mt-2 text-xs text-mute-600">{draft.footer}</p>
            ) : null}
          </div>
          {buttons.length > 0 ? (
            <div className="mt-1 flex flex-col gap-1">
              {buttons.map((text, index) => (
                <span
                  key={index}
                  className="border-2 border-rule bg-panel px-3 py-1.5 text-center text-sm text-brand-700"
                >
                  {text}
                </span>
              ))}
            </div>
          ) : null}
          <p className="mt-2 text-[11px] text-mute-600">{t("templates.previewNote")}</p>
        </aside>
      </div>
    </Dialog>
  );
}

/** Up to three one-tap answers under the message. Any tap opens the window. */
function QuickReplies({
  rows,
  disabled,
  onChange,
}: {
  rows: ButtonRow[];
  disabled: boolean;
  onChange: (rows: ButtonRow[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="kicker mb-1 text-mute-600">{t("templates.buttons")}</legend>
      {rows.map((row, index) => (
        <div key={row.key} className="flex flex-wrap items-center gap-2">
          <Input
            className="min-w-0 flex-[1_1_12rem]"
            maxLength={25}
            disabled={disabled}
            aria-label={t("templates.buttonN", { n: index + 1 })}
            value={row.text}
            onChange={(event) =>
              onChange(
                rows.map((other) =>
                  other.key === row.key
                    ? { ...other, text: event.target.value }
                    : other,
                ),
              )
            }
          />
          {disabled ? null : (
            <Button
              variant="ghost"
              aria-label={t("templates.removeButtonN", { n: index + 1 })}
              onClick={() => onChange(rows.filter((other) => other.key !== row.key))}
            >
              {t("templates.remove")}
            </Button>
          )}
        </div>
      ))}
      {disabled ? null : (
        <div>
          <Button
            variant="ghost"
            disabled={rows.length >= MAX_BUTTONS}
            onClick={() => onChange([...rows, { key: nextKey(), text: "" }])}
          >
            {t("templates.addButton")}
          </Button>
        </div>
      )}
      <p className="text-[11px] text-mute-600">{t("templates.buttonsHint")}</p>
    </fieldset>
  );
}
